import {Search} from 'lucide-react';
import {useCallback, useMemo, useState} from "react";
import {PhoneHQAvatar, PhoneHQButton, PhoneHQInput, PhoneHQLoader, PhoneHQSwitch} from 'ch3-ui-lib';
import {debounce} from "lodash";
import {
    useListEnsUserContactsQuery,
    useUpsertEnsUserContactMutation,
} from "../../../store/features/ens/ensService";
import {EnsUserContactListItem} from "../../../store/features/ens/types";
import {EnsPagination} from "../components/EnsPagination";
import {PhoneNumberInput} from "../../../components/Dialer/components/PhoneInput";

const PAGE_SIZE = 25;

interface RowState {
    msisdn: string;
    eligible: boolean;
    dirty: boolean;
}

const initialRowState = (item: EnsUserContactListItem): RowState => ({
    msisdn: item.externalMsisdn != null ? String(item.externalMsisdn) : '',
    eligible: item.ensEligible,
    dirty: false,
});

const ContactRow = ({item}: { item: EnsUserContactListItem }) => {
    const [state, setState] = useState<RowState>(initialRowState(item));
    const [save, {isLoading}] = useUpsertEnsUserContactMutation();

    const handleSave = async () => {
        const trimmed = state.msisdn.trim();
        const externalMsisdn = trimmed === '' ? null : Number(trimmed);
        if (externalMsisdn != null && Number.isNaN(externalMsisdn)) return;
        try {
            await save({userId: item.userId, externalMsisdn, ensEligible: state.eligible}).unwrap();
            setState((prev) => ({...prev, dirty: false}));
        } catch {
            // toast handled in service
        }
    };

    return (
        <tr className="border-t border-line">
            <td className="px-4 py-3 align-middle">
                <div className="flex items-center gap-3">
                    <PhoneHQAvatar src={item.avatarUrl ?? undefined} name={`${item.firstName} ${item.lastName}`} size="32px"/>
                    <div>
                        <div className="text-sm font-medium">{item.firstName} {item.lastName}</div>
                        <div className="text-xs text-ink-muted">{item.email}</div>
                        {item.position && <div className="text-xs text-ink-muted">{item.position}</div>}
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 align-middle w-64">
                <PhoneNumberInput
                    phqId={`ensContacts-msisdn-${item.userId}`}
                    phoneNumber={state.msisdn || undefined}
                    onPhoneNumberChange={(value) =>
                        setState((prev) => ({...prev, msisdn: value.toString(), dirty: true}))
                    }
                    onPhoneNumberClear={() =>
                        setState((prev) => ({...prev, msisdn: '', dirty: true}))
                    }
                />
            </td>
            <td className=" py-3 align-middle w-40 pr-10">
                <PhoneHQSwitch
                    label="Eligible"
                    checked={state.eligible}
                    onCheckedChange={() => setState((prev) => ({...prev, eligible: !prev.eligible, dirty: true}))}
                />
            </td>
            <td className="px-4 py-3 align-middle text-right w-32">
                <PhoneHQButton variant={state.dirty ? 'primary' : 'secondary'} size="sm" disabled={!state.dirty || isLoading} onClick={handleSave} phq-id={`ensContacts-saveBtn-${item.userId}`}>{isLoading ? 'Saving…' : 'Save'}</PhoneHQButton>
            </td>
        </tr>
    );
};

const EnsUserContactsAdmin = () => {
    const [phrase, setPhrase] = useState('');
    const [debouncedPhrase, setDebouncedPhrase] = useState('');
    const [page, setPage] = useState(0);

    const debouncePhrase = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedPhrase(value);
                setPage(0);
            }, 400),
        [],
    );

    const handlePhraseChange = useCallback(
        (value: string) => {
            setPhrase(value);
            debouncePhrase(value);
        },
        [debouncePhrase],
    );

    const {data, isFetching} = useListEnsUserContactsQuery({
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        phrase: debouncedPhrase || undefined,
    });

    const items = data?.data ?? [];
    const total = data?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="px-8 py-6 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h2 className="text-xl font-semibold">User emergency contacts</h2>
                    <p className="text-sm text-ink-muted">
                        Manage external contact phone numbers and eligibility for emergency notifications.
                    </p>
                </div>
                <PhoneHQInput
                    phq-id="ensContacts-search"
                    className="w-[320px]"
                    value={phrase}
                    placeholder="Search users"
                    prefixIcon={<Search size={16} aria-hidden="true" className="!p-0"/>}
                    onChange={(e) => handlePhraseChange(e.target.value)} onClear={() => handlePhraseChange('')}/>
            </div>

            <div className="border border-line rounded">
                {isFetching && (
                    <div className="flex items-center justify-center py-10">
                        <PhoneHQLoader/>
                    </div>
                )}
                {!isFetching && items.length === 0 && (
                    <div className="px-4 py-10 text-center text-sm text-ink-muted">No users match the search.</div>
                )}
                {!isFetching && items.length > 0 && (
                    <table className="w-full text-sm">
                        <thead className="text-xs text-ink-muted bg-surface-muted">
                        <tr>
                            <th className="text-left px-4 py-2">User</th>
                            <th className="text-left px-4 py-2">External contact phone number</th>
                            <th className="text-left px-4 py-2">Eligibility</th>
                            <th className="text-right px-4 py-2"/>
                        </tr>
                        </thead>
                        <tbody>
                        {items.map((item) => (
                            <ContactRow key={item.userId} item={item}/>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            {total > 0 && (
                    <EnsPagination
                        idPrefix="ensContacts"
                        page={page}
                        totalPages={totalPages}
                        isFetching={isFetching}
                        onChange={setPage}
                    />

            )}
        </div>
    );
};

export default EnsUserContactsAdmin;
